import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import ThemeToggle from './components/ThemeToggle';

const STATUS_MESSAGES = {
  PENDING: 'Tu cuenta está pendiente de activación.',
  SUSPENDED: 'Tu cuenta ha sido suspendida.',
  REJECTED: 'Tu cuenta ha sido rechazada. Contacta al administrador.',
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { signIn, resetPassword, isAuthenticated, userProfile, getRedirectPath, loading, profileLoading } = useAuth();

  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem('theme');
    if (stored) return stored === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)')?.matches;
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [statusBlock, setStatusBlock] = useState(null); // PENDING | SUSPENDED | REJECTED
  const [fieldErrors, setFieldErrors] = useState({});
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  // Apply theme
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) root.classList?.add('dark');
    else root.classList?.remove('dark');
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  // Auto-redirect if already authenticated
  useEffect(() => {
    if (!loading && !profileLoading && isAuthenticated && userProfile) {
      const status = userProfile?.status;
      if (status === 'PENDING' || status === 'SUSPENDED' || status === 'REJECTED') {
        setStatusBlock(status);
        return;
      }
      navigate(getRedirectPath(userProfile), { replace: true });
    }
  }, [isAuthenticated, userProfile, loading, profileLoading, navigate, getRedirectPath]);

  const validate = () => {
    const errors = {};
    if (!email?.trim()) errors.email = 'El correo electrónico es requerido';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email)) errors.email = 'Ingresa un correo válido';
    if (!password?.trim()) errors.password = 'La contraseña es requerida';
    else if (password?.length < 6) errors.password = 'Mínimo 6 caracteres';
    return errors;
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    const errors = validate();
    if (Object.keys(errors)?.length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setServerError('');
    setStatusBlock(null);
    setIsLoading(true);

    try {
      const { data, error } = await signIn(email, password);
      if (error) {
        if (error?.isStatusBlock) {
          // status is the message key: PENDING | SUSPENDED | REJECTED
          setStatusBlock(error?.message);
          return;
        }
        if (error?.isNetworkError || error?.message?.includes('fetch') || error?.message?.includes('network') || error?.message?.toLowerCase()?.includes('servidor')) {
          setServerError('No se pudo conectar al servidor. Verifica tu conexión e intenta de nuevo.');
        } else if (
          error?.message?.toLowerCase()?.includes('invalid') ||
          error?.message?.toLowerCase()?.includes('credentials') ||
          error?.message?.toLowerCase()?.includes('password') ||
          error?.status === 400
        ) {
          setServerError('Correo o contraseña incorrectos.');
        } else {
          setServerError(error?.message || 'Error al iniciar sesión. Intenta de nuevo.');
        }
        return;
      }

      const profile = data?.profile;
      if (!profile) {
        navigate('/administrative-dashboard', { replace: true });
        return;
      }

      navigate(getRedirectPath(profile), { replace: true });
    } catch (_err) {
      setServerError('Error inesperado. Intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  // Need direct supabase signout for status block case
  const supabaseSignOut = async () => {
    const { supabase } = await import('../../lib/supabase');
    await supabase?.auth?.signOut();
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    if (!forgotEmail?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(forgotEmail)) {
      return;
    }
    setForgotLoading(true);
    const { error } = await resetPassword(forgotEmail);
    setForgotLoading(false);
    if (!error) setForgotSent(true);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--color-background)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 0.8s linear infinite' }}>
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          <span style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)', fontSize: '0.875rem' }}>Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--color-background)', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
      {/* Decorative blobs — Vital teal */}
      <div aria-hidden="true" style={{ position: 'absolute', top: '-120px', right: '-120px', width: '480px', height: '480px', borderRadius: '50%', background: isDark ? 'radial-gradient(circle, rgba(10,191,204,0.15) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(0,188,212,0.10) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div aria-hidden="true" style={{ position: 'absolute', bottom: '-80px', left: '-80px', width: '360px', height: '360px', borderRadius: '50%', background: isDark ? 'radial-gradient(circle, rgba(0,139,154,0.12) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(0,139,154,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
      {/* Top bar — Vital branded */}
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', position: 'relative', zIndex: 10, backgroundColor: 'var(--color-card)', borderBottom: '2px solid var(--color-primary)', boxShadow: '0 2px 8px rgba(0,188,212,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img
            src="/assets/images/logo-1772764554588.png"
            alt="Vital Gestión en Salud logo"
            style={{ height: '32px', width: 'auto', objectFit: 'contain', flexShrink: 0 }}
          />
          <div>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 800, color: 'var(--color-foreground)', lineHeight: 1.1, display: 'block' }}>Update-vital-Ftech</span>
            <span style={{ fontFamily: 'var(--font-caption)', fontSize: '0.6875rem', color: 'var(--color-primary)', display: 'block', fontWeight: 500 }}>Distribución de Software</span>
          </div>
        </div>
        <ThemeToggle isDark={isDark} onToggle={() => setIsDark(v => !v)} />
      </header>
      {/* Main content */}
      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px 48px', position: 'relative', zIndex: 10 }}>
        <div style={{ width: '100%', maxWidth: '420px' }}>
          <div style={{ backgroundColor: 'var(--color-card)', borderRadius: 'var(--radius-xl)', boxShadow: '0 4px 24px rgba(0,188,212,0.12)', border: '1px solid var(--color-border)', padding: '36px 32px', width: '100%' }}>
            {/* Card header */}
            <div style={{ marginBottom: '28px', textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <img
                  src="/assets/images/vitaleditado-1772764451846.png"
                  alt="Vital Gestión en Salud logo"
                  style={{ width: '200px', height: 'auto', objectFit: 'contain' }}
                />
              </div>
              <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', fontWeight: 800, color: 'var(--color-foreground)', marginBottom: '6px', lineHeight: 1.2 }}>
                {forgotMode ? 'Recuperar Contraseña' : 'Bienvenido de nuevo'}
              </h1>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.9rem', color: 'var(--color-muted-foreground)', margin: 0 }}>
                {forgotMode ? 'Ingresa tu correo para recibir el enlace' : 'Ingresa a tu cuenta de Update-vital-Ftech'}
              </p>
            </div>

            {/* Status block alert */}
            {statusBlock && (
              <div style={{ backgroundColor: statusBlock === 'PENDING' ? 'rgba(217,119,6,0.08)' : 'rgba(220,38,38,0.08)', border: `1px solid ${statusBlock === 'PENDING' ? 'rgba(217,119,6,0.3)' : 'rgba(220,38,38,0.3)'}`, borderRadius: 'var(--radius-md)', padding: '12px 14px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <Icon name={statusBlock === 'PENDING' ? 'Clock' : 'XCircle'} size={16} color={statusBlock === 'PENDING' ? 'var(--color-warning)' : 'var(--color-error)'} />
                <p style={{ fontFamily: 'var(--font-caption)', fontSize: '0.8125rem', color: statusBlock === 'PENDING' ? 'var(--color-warning)' : 'var(--color-error)', margin: 0, lineHeight: 1.5 }}>
                  {STATUS_MESSAGES?.[statusBlock]}
                </p>
              </div>
            )}

            {/* Forgot password mode */}
            {forgotMode ? (
              <div>
                {forgotSent ? (
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{ width: '48px', height: '48px', backgroundColor: 'rgba(0,188,212,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                      <Icon name="CheckCircle" size={24} color="var(--color-primary)" />
                    </div>
                    <p style={{ fontFamily: 'var(--font-caption)', fontSize: '0.875rem', color: 'var(--color-foreground)', marginBottom: '8px', fontWeight: 600 }}>Correo enviado</p>
                    <p style={{ fontFamily: 'var(--font-caption)', fontSize: '0.8125rem', color: 'var(--color-muted-foreground)', marginBottom: '20px' }}>Revisa tu bandeja de entrada y sigue las instrucciones.</p>
                    <button onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', fontFamily: 'var(--font-caption)', fontSize: '0.875rem', fontWeight: 600 }}>Volver al inicio de sesión</button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} noValidate>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ fontFamily: 'var(--font-caption)', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-primary-dark)', display: 'block', marginBottom: '6px' }}>Correo electrónico</label>
                      <div style={{ position: 'relative' }}>
                        <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--color-primary)' }}><Icon name="Mail" size={16} color="currentColor" /></div>
                        <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e?.target?.value)} placeholder="tu@empresa.com" style={{ width: '100%', paddingLeft: '2.5rem', paddingRight: '1rem', paddingTop: '0.625rem', paddingBottom: '0.625rem', backgroundColor: 'var(--color-muted)', border: '1.5px solid var(--color-border)', borderRadius: 'var(--radius-md)', color: 'var(--color-foreground)', fontFamily: 'var(--font-body)', fontSize: '0.9375rem', outline: 'none', minHeight: '44px', transition: 'all 0.2s ease' }} onFocus={e => { e.target.style.borderColor = '#00BCD4'; e.target.style.boxShadow = '0 0 0 3px rgba(0,188,212,0.2)'; }} onBlur={e => { e.target.style.borderColor = 'var(--color-border)'; e.target.style.boxShadow = 'none'; }} />
                      </div>
                    </div>
                    <button type="submit" disabled={forgotLoading} style={{ width: '100%', padding: '0.75rem', backgroundColor: '#00BCD4', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-heading)', fontSize: '0.9375rem', fontWeight: 700, cursor: forgotLoading ? 'not-allowed' : 'pointer', minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: forgotLoading ? 0.7 : 1, transition: 'all 0.2s ease', boxShadow: '0 2px 8px rgba(0,188,212,0.3)' }} onMouseEnter={e => { if (!forgotLoading) { e.currentTarget.style.backgroundColor = '#008B9A'; } }} onMouseLeave={e => { if (!forgotLoading) { e.currentTarget.style.backgroundColor = '#00BCD4'; } }}>
                      {forgotLoading ? 'Enviando...' : 'Enviar enlace de recuperación'}
                    </button>
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                      <button type="button" onClick={() => setForgotMode(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', fontFamily: 'var(--font-caption)', fontSize: '0.8125rem', fontWeight: 500 }}>← Volver al inicio de sesión</button>
                    </div>
                  </form>
                )}
              </div>
            ) : (
              <form onSubmit={handleLogin} noValidate className="space-y-5">
                {/* Email */}
                <div className="space-y-1.5">
                  <label htmlFor="email" style={{ fontFamily: 'var(--font-caption)', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-primary-dark)', letterSpacing: '0.025em' }}>Correo electrónico</label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: fieldErrors?.email ? 'var(--color-error)' : 'var(--color-primary)' }}><Icon name="Mail" size={16} color="currentColor" /></div>
                    <input id="email" type="email" value={email} onChange={e => { setEmail(e?.target?.value); if (fieldErrors?.email) setFieldErrors(p => ({ ...p, email: '' })); }} placeholder="tu@empresa.com" autoComplete="email" style={{ width: '100%', paddingLeft: '2.5rem', paddingRight: '1rem', paddingTop: '0.625rem', paddingBottom: '0.625rem', backgroundColor: 'var(--color-muted)', border: `1.5px solid ${fieldErrors?.email ? 'var(--color-error)' : 'var(--color-border)'}`, borderRadius: 'var(--radius-md)', color: 'var(--color-foreground)', fontFamily: 'var(--font-body)', fontSize: '0.9375rem', outline: 'none', transition: 'all 0.2s ease', minHeight: '44px' }} onFocus={e => { e.target.style.borderColor = '#00BCD4'; e.target.style.boxShadow = '0 0 0 3px rgba(0,188,212,0.2)'; }} onBlur={e => { e.target.style.borderColor = fieldErrors?.email ? 'var(--color-error)' : 'var(--color-border)'; e.target.style.boxShadow = 'none'; }} />
                  </div>
                  {fieldErrors?.email && <p style={{ color: 'var(--color-error)', fontSize: '0.75rem', fontFamily: 'var(--font-caption)', display: 'flex', alignItems: 'center', gap: '4px' }}><Icon name="AlertCircle" size={12} color="currentColor" />{fieldErrors?.email}</p>}
                </div>

                {/* Password */}
                <div className="space-y-1.5">
                  <label htmlFor="password" style={{ fontFamily: 'var(--font-caption)', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-primary-dark)', letterSpacing: '0.025em' }}>Contraseña</label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: fieldErrors?.password ? 'var(--color-error)' : 'var(--color-primary)' }}><Icon name="Lock" size={16} color="currentColor" /></div>
                    <input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => { setPassword(e?.target?.value); if (fieldErrors?.password) setFieldErrors(p => ({ ...p, password: '' })); }} placeholder="••••••••" autoComplete="current-password" style={{ width: '100%', paddingLeft: '2.5rem', paddingRight: '3rem', paddingTop: '0.625rem', paddingBottom: '0.625rem', backgroundColor: 'var(--color-muted)', border: `1.5px solid ${fieldErrors?.password ? 'var(--color-error)' : 'var(--color-border)'}`, borderRadius: 'var(--radius-md)', color: 'var(--color-foreground)', fontFamily: 'var(--font-body)', fontSize: '0.9375rem', outline: 'none', transition: 'all 0.2s ease', minHeight: '44px' }} onFocus={e => { e.target.style.borderColor = '#00BCD4'; e.target.style.boxShadow = '0 0 0 3px rgba(0,188,212,0.2)'; }} onBlur={e => { e.target.style.borderColor = fieldErrors?.password ? 'var(--color-error)' : 'var(--color-border)'; e.target.style.boxShadow = 'none'; }} />
                    <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '28px', minHeight: '28px' }} aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                      <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} color="currentColor" />
                    </button>
                  </div>
                  {fieldErrors?.password && <p style={{ color: 'var(--color-error)', fontSize: '0.75rem', fontFamily: 'var(--font-caption)', display: 'flex', alignItems: 'center', gap: '4px' }}><Icon name="AlertCircle" size={12} color="currentColor" />{fieldErrors?.password}</p>}
                </div>

                {/* Server error */}
                {serverError && (
                  <div style={{ backgroundColor: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 'var(--radius-md)', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-error)', fontSize: '0.875rem', fontFamily: 'var(--font-caption)' }}>
                    <Icon name="AlertCircle" size={15} color="currentColor" />
                    {serverError}
                  </div>
                )}

                {/* Submit */}
                <button type="submit" disabled={isLoading} style={{ width: '100%', padding: '0.75rem 1.5rem', backgroundColor: isLoading ? 'var(--color-muted-foreground)' : '#00BCD4', color: '#FFFFFF', border: 'none', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-heading)', fontSize: '0.9375rem', fontWeight: 700, cursor: isLoading ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '48px', boxShadow: isLoading ? 'none' : '0 2px 12px rgba(0,188,212,0.35)', letterSpacing: '0.01em' }} onMouseEnter={e => { if (!isLoading) { e.currentTarget.style.backgroundColor = '#008B9A'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,188,212,0.45)'; } }} onMouseLeave={e => { if (!isLoading) { e.currentTarget.style.backgroundColor = '#00BCD4'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,188,212,0.35)'; } }}>
                  {isLoading ? (
                    <><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 0.8s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>Iniciando sesión...</>
                  ) : (
                    <><Icon name="LogIn" size={17} color="currentColor" />Iniciar Sesión</>
                  )}
                </button>

                {/* Forgot password */}
                <div style={{ textAlign: 'center' }}>
                  <button type="button" onClick={() => setForgotMode(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-caption)', fontSize: '0.8125rem', color: 'var(--color-primary)', fontWeight: 500, padding: '4px 8px', borderRadius: 'var(--radius-sm)', minHeight: '36px' }}>
                    Olvidé mi contraseña
                  </button>
                </div>

                {/* Divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '4px 0' }}>
                  <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--color-border)' }} />
                  <span style={{ fontFamily: 'var(--font-caption)', fontSize: '0.75rem', color: 'var(--color-muted-foreground)', whiteSpace: 'nowrap' }}>¿Nuevo en Update-vital-Ftech?</span>
                  <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--color-border)' }} />
                </div>

                {/* Register */}
                <Link to="/user-registration" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', padding: '0.625rem 1.5rem', backgroundColor: 'transparent', color: '#00BCD4', border: '1.5px solid #00BCD4', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-heading)', fontSize: '0.9rem', fontWeight: 700, textDecoration: 'none', transition: 'all 0.2s ease', minHeight: '44px' }} onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#00BCD4'; e.currentTarget.style.color = '#FFFFFF'; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#00BCD4'; }}>
                  <Icon name="UserPlus" size={16} color="currentColor" />
                  ¿No tienes cuenta? Regístrate
                </Link>
              </form>
            )}
          </div>

          <p style={{ textAlign: 'center', marginTop: '20px', fontFamily: 'var(--font-caption)', fontSize: '0.75rem', color: 'var(--color-muted-foreground)' }}>
            Al iniciar sesión aceptas nuestros{' '}
            <span style={{ color: 'var(--color-primary)', cursor: 'pointer' }}>Términos de Servicio</span>{' '}y{' '}
            <span style={{ color: 'var(--color-primary)', cursor: 'pointer' }}>Política de Privacidad</span>
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
